import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { apiErrorMessage } from "@/lib/api/errors";
import { useOfflineCreate, type OfflineCreateVars } from "@/lib/api/offline-mutations";
import {
  applyOptimistic,
  rollbackOptimistic,
  type OptimisticSnapshot,
} from "@/lib/api/optimistic";

import { appointmentsApi } from "./api";
import type { Appointment, AppointmentStatus } from "./types";

const KEY = "appointments";

/** Clé de mutation de création — partagée avec le registre offline. */
export const APPOINTMENTS_CREATE_KEY = [KEY, "create"] as const;

type AppointmentCreatePayload = Parameters<typeof appointmentsApi.create>[0];

export function useAppointments(
  params: Parameters<typeof appointmentsApi.list>[0] = {},
  opts: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [KEY, "list", params],
    queryFn: () => appointmentsApi.list(params),
    enabled: opts.enabled ?? true,
    // Garde la page précédente à l'écran pendant le chargement de la suivante :
    // changer de page ou de filtre n'efface plus la liste, donc aucun état de
    // chargement n'apparaît. `isPlaceholderData` signale que l'affichage est
    // encore celui d'avant.
    placeholderData: (prev) => prev,
  });
}

/** Créneaux occupés d'un praticien pour une journée (choix d'un créneau libre). */
export function useAvailability(practitioner: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: [KEY, "availability", practitioner, date],
    queryFn: () => appointmentsApi.availability(practitioner!, date!),
    enabled: !!practitioner && !!date,
    staleTime: 15_000,
  });
}

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: [KEY, "detail", id],
    queryFn: () => appointmentsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  const mutation = useMutation<Appointment, unknown, OfflineCreateVars<AppointmentCreatePayload>>({
    mutationKey: [...APPOINTMENTS_CREATE_KEY],
    mutationFn: ({ payload, idempotencyKey }) => appointmentsApi.create(payload, idempotencyKey),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Rendez-vous créé.");
    },
  });
  return useOfflineCreate(mutation);
}

/** Statut atteint par chaque transition — sert au rendu optimiste. */
const TRANSITION_TARGET: Record<"confirm" | "start" | "complete" | "no-show", AppointmentStatus> = {
  confirm: "confirmed",
  start: "in_progress",
  complete: "completed",
  "no-show": "no_show",
};

export function useTransition(id: string, action: "confirm" | "start" | "complete" | "no-show") {
  const qc = useQueryClient();
  return useMutation<Appointment, unknown, void, OptimisticSnapshot>({
    mutationFn: () => {
      if (action === "confirm") return appointmentsApi.confirm(id);
      if (action === "start") return appointmentsApi.start(id);
      if (action === "complete") return appointmentsApi.complete(id);
      return appointmentsApi.noShow(id);
    },
    // Le badge bascule sans attendre le serveur. Sur un planning parcouru au
    // rythme des arrivées patients, cet aller-retour était le principal
    // ressenti de lenteur de l'application.
    onMutate: () =>
      applyOptimistic<Appointment>(qc, [KEY], id, (appointment) => ({
        ...appointment,
        status: TRANSITION_TARGET[action],
      })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Rendez-vous mis à jour.");
    },
    onError: (err, _vars, snapshot) => {
      // Transition refusée par la machine d'état : on remet l'écran d'aplomb.
      rollbackOptimistic(qc, snapshot);
      toast.error(apiErrorMessage(err, "Transition impossible (état incompatible)."));
    },
  });
}

export function useRemindAppointment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => appointmentsApi.remind(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Rappel envoyé au patient.");
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à l'envoi du rappel."));
    },
  });
}

export function useCancelAppointment(id: string) {
  const qc = useQueryClient();
  return useMutation<Appointment, unknown, string, OptimisticSnapshot>({
    mutationFn: (reason: string) => appointmentsApi.cancel(id, reason),
    onMutate: () =>
      applyOptimistic<Appointment>(qc, [KEY], id, (appointment) => ({
        ...appointment,
        status: "cancelled",
      })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Rendez-vous annulé.");
    },
    onError: (err, _reason, snapshot) => {
      rollbackOptimistic(qc, snapshot);
      toast.error(apiErrorMessage(err, "Erreur à l'annulation du rendez-vous."));
    },
  });
}

/**
 * Suppression d'un rendez-vous.
 *
 * Distincte de l'annulation : un rendez-vous annulé reste rattaché au patient
 * et continue de bloquer la suppression de sa fiche. Sans cette action, un
 * patient ayant eu ne serait-ce qu'un rendez-vous devenait indélébile depuis
 * l'interface — l'endpoint et la permission `appointments.delete` existaient
 * pourtant déjà côté serveur.
 */
export function useDeleteAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => appointmentsApi.remove(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [KEY] });
      toast.success("Rendez-vous supprimé.");
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, "Erreur à la suppression du rendez-vous."));
    },
  });
}
